/**
 * `<TransportIllustration>` — compact hand-drawn line-art for the
 * three TransportPicker / TransportChooser cards.
 *
 * # Why this exists
 *
 * The picker cards previously used a single 20px lucide-react
 * icon in the corner of each card. The 2026-05-14 illustration
 * audit caught that first-time users at a choice point have no
 * mental model for "what's behind this option" — the cards read
 * as text blocks with a decorative icon, not as three concrete
 * paths through the flow. This component adds a 64–72px line-
 * art illustration inside each card that depicts the actual
 * thing that's going to happen: a phone showing a QR code with
 * Bluetooth waves, a laptop with a biometric prompt, or a USB
 * security key glowing.
 *
 * # Style family
 *
 * Same hand-drawn rules as `<EmptyStateHero>`: stroke=1.8,
 * round caps + joins, primary token for accent shapes, no raw
 * palette values. The illustration shape sits inside a soft
 * primary-tinted halo so each card reads as a discrete "thing."
 *
 * # Behaviour
 *
 *   - `transport` selects the variant (other_device → phone+QR,
 *     this_device → laptop+biometric, security_key → USB key).
 *   - `active` tints the halo + accents more strongly so the
 *     selected card's illustration reflects the picker state.
 *   - `disabled` mutes the illustration so it doesn't pull
 *     focus on an unreachable card.
 *
 * Pure presentation — no state, no side effects.
 */

import type { ReactElement } from "react";
import type { EnrollmentChoice } from "@pureid/auth-core";

import { cn } from "../../lib/utils";

export interface TransportIllustrationProps {
  transport: EnrollmentChoice;
  /** Whether this card is currently selected. Drives accent
   *  saturation (selected = primary, unselected = muted). */
  active?: boolean;
  /** Whether this card is unreachable. Mutes everything so the
   *  illustration doesn't pull focus on a disabled option. */
  disabled?: boolean;
  className?: string;
}

export function TransportIllustration({
  transport,
  active = false,
  disabled = false,
  className,
}: TransportIllustrationProps): ReactElement {
  // Stroke colour — keep the same line tone across active and
  // unselected states so the illustration's silhouette doesn't
  // "jump" when the user changes their pick. The accent shape
  // (halo, biometric glyph, QR pattern fill) is what shifts.
  const stroke = disabled
    ? "hsl(var(--muted-foreground) / 0.55)"
    : "hsl(var(--foreground))";
  const accent = disabled
    ? "hsl(var(--muted-foreground) / 0.35)"
    : active
      ? "hsl(var(--primary))"
      : "hsl(var(--primary) / 0.7)";
  const haloFill = disabled
    ? "hsl(var(--muted) / 0.4)"
    : active
      ? "hsl(var(--primary) / 0.12)"
      : "hsl(var(--primary) / 0.06)";

  return (
    <figure
      className={cn("inline-block", className)}
      aria-hidden="true"
      style={{ margin: 0 }}
    >
      <svg
        viewBox="0 0 140 80"
        width="100%"
        height="auto"
        xmlns="http://www.w3.org/2000/svg"
      >
        {renderVariant(transport, { stroke, accent, haloFill })}
      </svg>
    </figure>
  );
}

interface PaletteContext {
  stroke: string;
  accent: string;
  haloFill: string;
}

function renderVariant(
  transport: EnrollmentChoice,
  c: PaletteContext,
): ReactElement {
  switch (transport) {
    case "other_device":
      return <PhoneWithQR {...c} />;
    case "this_device":
      return <LaptopWithBiometric {...c} />;
    case "security_key":
      return <UsbSecurityKey {...c} />;
    case "pureid_qr":
      // Phase G — PureID-QR is the same visual model as
      // other_device from the picker user's POV (phone with
      // QR), so reuse the illustration. The semantic difference
      // (PureID's own channel vs OS-mediated hybrid) is captured
      // in the card's title/subtitle, not the illustration.
      return <PhoneWithQR {...c} />;
    case "any":
      // "No preference" has no destination to draw, and drawing
      // one would be the illustration promising what the card
      // deliberately does not. The laptop is the honest floor:
      // whatever the OS prompt lands on, the ceremony runs HERE
      // (unlike pureid_qr, which runs on the phone).
      return <LaptopWithBiometric {...c} />;
  }
}

/**
 * Phone outline at a soft tilt showing a QR pattern on its
 * screen, with two Bluetooth-style wave arcs emanating from the
 * top-right to suggest the cross-device pairing. Sits inside a
 * primary halo that names "this option is reachable."
 */
function PhoneWithQR({ stroke, accent, haloFill }: PaletteContext): ReactElement {
  return (
    <g
      fill="none"
      stroke={stroke}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Soft halo behind the phone. */}
      <ellipse cx="48" cy="42" rx="32" ry="28" fill={haloFill} stroke="none" />

      {/* Phone body — slight clockwise tilt for visual
          interest; rounded rectangle. */}
      <g transform="rotate(-6 48 42)">
        <rect
          x="34"
          y="18"
          width="28"
          height="48"
          rx="4"
          fill="hsl(var(--card))"
          stroke={accent}
        />
        {/* Screen */}
        <rect
          x="37"
          y="22"
          width="22"
          height="36"
          rx="1.5"
          fill="hsl(var(--background))"
          stroke={stroke}
          strokeWidth="0.8"
        />
        {/* QR pattern — abstract 4x4 grid; some filled, some
            outlined, to suggest a code without spelling one.
            The 3 corner squares are deliberate (real QR has
            position markers). */}
        <g fill={accent} stroke="none">
          {/* Top-left position marker */}
          <rect x="39" y="24" width="5" height="5" rx="0.5" />
          <rect x="40.5" y="25.5" width="2" height="2" fill="hsl(var(--background))" />
          {/* Top-right position marker */}
          <rect x="52" y="24" width="5" height="5" rx="0.5" />
          <rect x="53.5" y="25.5" width="2" height="2" fill="hsl(var(--background))" />
          {/* Bottom-left position marker */}
          <rect x="39" y="37" width="5" height="5" rx="0.5" />
          <rect x="40.5" y="38.5" width="2" height="2" fill="hsl(var(--background))" />
          {/* Data scatter — uniform rectangles for an abstract
              "this is QR-ish" read. */}
          <rect x="46" y="25" width="2" height="2" />
          <rect x="49" y="27" width="2" height="2" />
          <rect x="46" y="30" width="2" height="2" />
          <rect x="49" y="32" width="2" height="2" />
          <rect x="46" y="34" width="2" height="2" />
          <rect x="46" y="44" width="2" height="2" />
          <rect x="50" y="44" width="2" height="2" />
          <rect x="54" y="44" width="2" height="2" />
          <rect x="46" y="48" width="2" height="2" />
          <rect x="54" y="48" width="2" height="2" />
          <rect x="50" y="51" width="2" height="2" />
        </g>
        {/* Home indicator on the phone bottom */}
        <line
          x1="44"
          y1="62"
          x2="52"
          y2="62"
          stroke={stroke}
          strokeWidth="1.2"
        />
      </g>

      {/* Bluetooth wave arcs to the right of the phone,
          suggesting cross-device pairing. Two arcs of
          increasing radius. */}
      <g stroke={accent} fill="none" strokeWidth="1.5">
        <path d="M 80 32 Q 86 42, 80 52" strokeDasharray="0" />
        <path d="M 90 28 Q 100 42, 90 56" strokeDasharray="3 3" opacity="0.65" />
      </g>

      {/* Small target device on the far right — abstract
          monitor outline to imply "the device receiving the
          paired sign-in." */}
      <g stroke={stroke} fill="hsl(var(--card))" strokeWidth="1.4">
        <rect x="108" y="32" width="22" height="14" rx="1.5" />
        <line x1="113" y1="48" x2="125" y2="48" />
        <line x1="116" y1="46" x2="122" y2="46" strokeWidth="2" />
      </g>
    </g>
  );
}

/**
 * Laptop outline (open at a comfortable angle) showing a
 * fingerprint sensor glyph on its screen, conveying "use the
 * device you're sitting at." A short halo behind the laptop
 * matches the family palette.
 */
function LaptopWithBiometric({
  stroke,
  accent,
  haloFill,
}: PaletteContext): ReactElement {
  return (
    <g
      fill="none"
      stroke={stroke}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Halo */}
      <ellipse cx="70" cy="42" rx="46" ry="28" fill={haloFill} stroke="none" />

      {/* Laptop screen */}
      <rect
        x="36"
        y="18"
        width="68"
        height="38"
        rx="2"
        fill="hsl(var(--card))"
        stroke={accent}
      />
      <rect
        x="40"
        y="22"
        width="60"
        height="30"
        rx="1"
        fill="hsl(var(--background))"
        stroke={stroke}
        strokeWidth="0.6"
      />

      {/* Fingerprint glyph on the screen — concentric arcs
          that read as a fingerprint without the ridge density
          that would clutter the small canvas. */}
      <g
        transform="translate(70 37)"
        stroke={accent}
        strokeWidth="1.5"
        fill="none"
      >
        <path d="M -10 4 Q -10 -6, 0 -8 Q 10 -6, 10 4" />
        <path d="M -7 5 Q -7 -3, 0 -5 Q 7 -3, 7 5" />
        <path d="M -4 6 Q -4 -1, 0 -2 Q 4 -1, 4 6" />
        <path d="M -1 6 L -1 1" />
        <circle cx="0" cy="-2" r="0.8" fill={accent} stroke="none" />
      </g>

      {/* Laptop base / hinge */}
      <path
        d="M 28 58 L 112 58 Q 116 58, 114 62 L 26 62 Q 24 58, 28 58 Z"
        fill="hsl(var(--card))"
        stroke={stroke}
      />
      <line
        x1="64"
        y1="60"
        x2="76"
        y2="60"
        stroke={stroke}
        strokeWidth="0.6"
        opacity="0.5"
      />
    </g>
  );
}

/**
 * USB-shaped hardware security key at a slight angle with a
 * subtle glow halo around the connector tip to suggest
 * "active / reachable." The body has a small NFC arc on its
 * face to imply contactless capability where the key supports
 * it.
 */
function UsbSecurityKey({
  stroke,
  accent,
  haloFill,
}: PaletteContext): ReactElement {
  return (
    <g
      fill="none"
      stroke={stroke}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {/* Glow halo at the connector end */}
      <ellipse cx="42" cy="42" rx="22" ry="16" fill={haloFill} stroke="none" />

      {/* Key body */}
      <g transform="rotate(-8 70 42)">
        {/* Plastic body */}
        <rect
          x="48"
          y="34"
          width="56"
          height="18"
          rx="2"
          fill="hsl(var(--card))"
          stroke={stroke}
        />
        {/* Capacitive touch button — slightly raised circle */}
        <circle
          cx="68"
          cy="43"
          r="4.5"
          fill="hsl(var(--background))"
          stroke={accent}
          strokeWidth="1.6"
        />
        <circle cx="68" cy="43" r="1.5" fill={accent} stroke="none" />

        {/* NFC arcs above the body to imply contactless
            ability — two short arcs that read as wave fronts
            without being a full Wi-Fi cone. */}
        <g
          stroke={accent}
          strokeWidth="1.4"
          fill="none"
          opacity="0.85"
        >
          <path d="M 86 32 Q 90 36, 86 40" />
          <path d="M 90 28 Q 96 36, 90 44" strokeDasharray="2 2" opacity="0.7" />
        </g>

        {/* USB-A connector tip — a smaller rectangle that
            protrudes from the left of the body. */}
        <rect
          x="36"
          y="38"
          width="14"
          height="10"
          rx="0.5"
          fill="hsl(var(--card))"
          stroke={stroke}
        />
        {/* Connector inside — three short strokes to suggest
            contacts. */}
        <g stroke={stroke} strokeWidth="1">
          <line x1="40" y1="40" x2="40" y2="46" />
          <line x1="43" y1="40" x2="43" y2="46" />
          <line x1="46" y1="40" x2="46" y2="46" />
        </g>

        {/* Keyring hole on the right end */}
        <circle
          cx="100"
          cy="43"
          r="2"
          fill="hsl(var(--background))"
          stroke={stroke}
        />
      </g>
    </g>
  );
}
